import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import { skip } from 'rxjs/operators';

import { EnvironmentService } from 'src/services/environment/environment.service';
import type { IConsentState } from 'src/services/personal-data-protection/types';

import { FALLBACK_CONSENT_STATE, UC_SERVICE_TO_CONSENT_CAT, UC_SERVICES } from './entities/const';
import { UserCentricsStateConvertorService } from './user-centrics-state-convertor.service';

declare const window;

const LOAD_TIMEOUT = 20000;
const CHECK_LOAD_INTERVAL = 100;

@Injectable()
export class UserCentricsService {
    private readonly userCentricsStateConvertorService = inject(UserCentricsStateConvertorService);
    private readonly cookieService = inject(CookieService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly environmentService = inject(EnvironmentService);
    private readonly document = inject(DOCUMENT);

    private isLoadingFailed = false;
    private stateSubject = new BehaviorSubject<IConsentState>(FALLBACK_CONSENT_STATE);
    private readonly cookieStateKey = 'pdpm_consent_state';

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            window.addEventListener('UC_UI_VIEW_CHANGED', (event) => this.onUIViewChanged(event));

            // fallback for slow connection
            const ucEventFromDataLayer = this.getUserCentricsEventFromDataLayer();
            if (ucEventFromDataLayer) {
                this.onUpdateConsentDecision({ detail: ucEventFromDataLayer });
            }

            this.stateSubject.next(this.loadConsentState());
            window.addEventListener('user_centrics_event', (event) => this.onUpdateConsentDecision(event), false);
        }
    }

    private onUIViewChanged(event: any) {
        const view = event?.detail?.view;
        if (!view || !this.document?.body) {
            return;
        }

        if (view === 'NONE') {
            this.document.body.classList.remove('overflowHidden');
        } else {
            this.document.body.classList.add('overflowHidden');
        }
    }

    private onUpdateConsentDecision(event) {
        try {
            if (event.detail?.event === 'consent_status') {
                const convertedState = this.userCentricsStateConvertorService.convert(event);
                this.saveConsentState(convertedState);
                this.stateSubject.next(convertedState);
            }
        } catch (e) {
            console.error(e);
        }
    }

    public get changeConsentState$() {
        return this.stateSubject.asObservable().pipe(skip(1));
    }

    public openCookiePreferences() {
        isPlatformBrowser(this.platformId) && window.UC_UI?.showSecondLayer();
    }

    public getConsentState(): IConsentState {
        if (isPlatformBrowser(this.platformId)) {
            return this.stateSubject.value || FALLBACK_CONSENT_STATE;
        }

        return FALLBACK_CONSENT_STATE;
    }

    public isUserCentricsLoaded(): boolean {
        return isPlatformBrowser(this.platformId) && window?.UC_UI?.isInitialized?.();
    }

    public isUserCentricsLoadedAsync() {
        if (isPlatformBrowser(this.platformId) && !this.isLoadingFailed) {
            return new Promise((resolve) => {
                if (!this.isUserCentricsLoaded()) {
                    let checkCounter = 0;

                    const intervalId = setInterval(() => {
                        if (this.isUserCentricsLoaded()) {
                            // performance.mark('usercentrics-loaded');
                            // performance.measure('ucMeasurement', 'page-load-start', 'usercentrics-loaded');
                            // const measurement = performance.getEntriesByName('ucMeasurement')[0];
                            // console.log('Time to load UserCentrics:', measurement.duration);

                            clearInterval(intervalId);
                            resolve(true);
                        } else if (++checkCounter > LOAD_TIMEOUT / CHECK_LOAD_INTERVAL) {
                            clearInterval(intervalId);
                            this.isLoadingFailed = true;
                            resolve(false);
                        }
                    }, CHECK_LOAD_INTERVAL);
                } else {
                    resolve(true);
                }
            });
        }

        return Promise.resolve(false);
    }

    private saveConsentState(convertedState: IConsentState) {
        try {
            const state = Object.keys(convertedState)
                .filter((key) => convertedState[key])
                .join('.');

            // we need to store current state in cookies, the format is numbers divided by dot.
            this.cookieService.set(this.cookieStateKey, state, {
                path: '/',
                domain: this.environmentService.environment.cookieDomain,
                expires: 365,
            });
        } catch (e) {
            console.warn('Unable to save consent state');
        }
    }

    private loadConsentState(): IConsentState {
        try {
            const serializedState =
                this.cookieService.get(this.cookieStateKey) || String(UC_SERVICE_TO_CONSENT_CAT[UC_SERVICES.Required]);
            const consents = serializedState.split('.');
            return consents.reduce((accumulator, key) => {
                accumulator[key] = true;
                return accumulator;
            }, {});
        } catch (e) {
            console.warn('Unable to load consent state', e);
            return null;
        }
    }

    private getUserCentricsEventFromDataLayer() {
        const dataLayer = window.dataLayer || [];
        return dataLayer.find((entry) => entry.action === 'onInitialPageLoad' && entry.event === 'consent_status');
    }
}
